package org.teddapp.project.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.ReadOnlyComposable
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.unit.dp

/**
 * Tokens TEDD — surface d'accès aux tokens du design system.
 *
 * Les couleurs sont des getters @Composable qui lisent `LocalTeddColors`
 * (cf. `TeddColors.kt`, où chaque token est documenté). Rayons, espacements et
 * tailles sont des constantes : voir aussi [TeddSpace] et [TeddSize].
 */
object TeddTokens {
    // ── couleurs du design system ─────────────────────────────────────
    val Bg: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.bg
    val Surface: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.surface
    val SurfaceSunken: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.surfaceSunken
    val SurfaceOverlay: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.surfaceOverlay
    val Line: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.line
    val LineStrong: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.lineStrong
    val Ink: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.ink
    val InkMuted: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.inkMuted
    val InkSubtle: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.inkSubtle
    val Action: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.action
    val OnAction: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.onAction
    val ActionHover: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.actionHover
    val Lime: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.lime
    val OnLime: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.onLime
    val LimeStrong: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.limeStrong
    val LimeSoft: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.limeSoft
    val Laterite: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.laterite
    val OnLaterite: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.onLaterite
    val LateriteSoft: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.lateriteSoft
    val Success: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.success
    val SuccessSoft: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.successSoft
    val Warning: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.warning
    val WarningSoft: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.warningSoft
    val Danger: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.danger
    val DangerSoft: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.dangerSoft
    val OnDanger: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.onDanger
    val FocusRing: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.focusRing
    val Link: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.link
    val ModToday: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.modToday
    val ModFocus: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.modFocus
    val ModPortfolio: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.modPortfolio
    val ModGoals: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.modGoals
    val ModNotes: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.modNotes
    val ModHabits: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.modHabits
    val ModFinance: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.modFinance
    val ModInbox: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.modInbox
    val Bronze: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.bronze
    val Argent: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.argent
    val Or: Color
        @Composable @ReadOnlyComposable
        get() = LocalTeddColors.current.or

    // ── rayons emboîtés ───────────────────────────────────────────────
    /** Case à cocher, touche clavier, cellule de grille. */
    val RXs = 5.dp
    /** Boutons, champs, onglets, éléments de navigation. */
    val RSm = 10.dp
    /** Cartes internes, lignes sélectionnées, flashcards. */
    val RMd = 14.dp
    /** Tuiles, palette, modales. */
    val RLg = 22.dp
    /** Tags, pastilles, avatars. */
    val RFull = 999.dp
    /** Anciens noms : le design system s'arrête à [RLg]. */
    val RXl = RLg
    val R2xl = RLg

    // ── anciens dégradés ──────────────────────────────────────────────
    // Le design system n'a pas de dégradé : ces pinceaux sont devenus des aplats,
    // le temps de reprendre les écrans qui les utilisent.
    val SignatureGradient: Brush
        @Composable @ReadOnlyComposable
        get() = SolidColor(Action)
    val SignatureGradientSoft: Brush
        @Composable @ReadOnlyComposable
        get() = SolidColor(LimeSoft)
    // Le logo garde ses couleurs d'origine (bleu de Prusse, plus clair en sombre) : la
    // marque ne suit pas la palette du design system.
    val LogoGradient: Brush
        @Composable @ReadOnlyComposable
        get() = Brush.linearGradient(
            colors = if (LocalTeddColors.current.isDark) {
                listOf(Color(0xFF6F98D1), Color(0xFF8FB3E6), Color(0xFFA9C4EA))
            } else {
                listOf(Color(0xFF1B3F6E), Color(0xFF22508A), Color(0xFF4A78B5))
            },
            start = Offset.Zero,
            end = Offset.Infinite,
        )
    val LogoArcGradient: Brush
        @Composable @ReadOnlyComposable
        get() = Brush.linearGradient(
            colors = listOf(Color.White.copy(alpha = 0.92f), Color.White.copy(alpha = 0.70f)),
            start = Offset.Zero,
            end = Offset.Infinite,
        )
    val LoginRadialIndigo: Brush
        @Composable @ReadOnlyComposable
        get() = SolidColor(Color.Transparent)
    val LoginRadialCyan: Brush
        @Composable @ReadOnlyComposable
        get() = SolidColor(Color.Transparent)
}

/** Espacements, base 4 dp. */
object TeddSpace {
    /** Écart icône–texte, intérieur des tags. */
    val S1 = 4.dp
    /** Écart entre contrôles voisins. */
    val S2 = 8.dp
    /** Padding horizontal des boutons, écart dans une ligne de tâche. */
    val S3 = 12.dp
    /** Padding des cartes compactes, gouttière de la grille bento. */
    val S4 = 16.dp
    /** Padding des tuiles. */
    val S5 = 20.dp
    /** Padding des panneaux. */
    val S6 = 24.dp
    /** Entre sections d'un écran. */
    val S8 = 32.dp
    /** Marge haute d'écran. */
    val S12 = 48.dp
    val S16 = 64.dp
}

/** Tailles fixes. */
object TeddSize {
    /** Boutons et champs compacts (barres d'outils). */
    val ControlSm = 32.dp
    /** Hauteur par défaut des boutons et champs. */
    val ControlMd = 40.dp
    /** Hauteur tactile, et bouton principal d'une session Focus. */
    val ControlLg = 48.dp
    /** Largeur de la barre latérale desktop. */
    val Sidebar = 248.dp
    /** Largeur maximale de lecture (notes). */
    val Measure = 680.dp
    /** Largeur des panneaux latéraux (détail, formulaires). */
    val SidePanel = 480.dp
}
